"use client";

import { useState, useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  ArrowRight,
  CircleAlert,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
} from "lucide-react";

import {
  loginErrorFromQuery,
  loginErrorMessage,
} from "@/lib/auth/login-errors";
import { recordLogin } from "@/lib/auth/record-login";
import { createClient } from "@/lib/supabase/client";
import Banner from "@/components/ds/Banner";
import Button from "@/components/ds/Button";
import { Field, TextInput } from "@/components/ds/Field";

// Chave estável do localStorage onde guardamos APENAS o email (nunca a senha)
// quando o usuário marca "Lembrar de mim".
const REMEMBER_EMAIL_KEY = "obra-centtra:remember-email";

function LoginForm() {
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirect") ?? "/";
  const errorFromQuery = loginErrorFromQuery(searchParams.get("error"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(errorFromQuery);
  const [loading, setLoading] = useState(false);

  // Ao montar: se houver email salvo, pré-preenche o campo e marca o checkbox.
  // Roda só no client (efeito), então não causa mismatch de hidratação.
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(REMEMBER_EMAIL_KEY);
      if (saved) {
        setEmail(saved);
        setRemember(true);
      }
    } catch {
      // localStorage indisponível (modo privado/SSR) — ignora.
    }
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    let authError: { message?: string } | null = null;
    try {
      const supabase = createClient();
      ({ error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      }));
    } catch {
      // Falha inesperada (rede/cliente) — destrava o botão e avisa.
      setLoading(false);
      setError("Não foi possível entrar. Tente novamente.");
      return;
    }

    if (authError) {
      setLoading(false);
      setError(loginErrorMessage(authError.message));
      return;
    }

    // Persiste APENAS o email (nunca a senha) conforme a escolha do usuário.
    // A sessão em si já é mantida pelo @supabase/ssr via cookie/refresh token —
    // não há configuração de duração aqui; o ganho deste checkbox é só
    // pré-preencher o email + ajudar o gerenciador de senhas do navegador.
    try {
      if (remember) {
        window.localStorage.setItem(REMEMBER_EMAIL_KEY, email);
      } else {
        window.localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
    } catch {
      // localStorage indisponível — segue o login normalmente.
    }

    try {
      await recordLogin();
    } catch {
      // telemetria best-effort
    }

    // Hard navigation pra o middleware ver os cookies frescos (ver nota
    // histórica: SPA navigation corre com o cache RSC sem o cookie).
    window.location.assign(redirectTo);
  }

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-caps text-ink uppercase">
        Entre na sua conta
      </h1>
      <p className="mt-1 text-sm text-ink-2">
        Bem-vindo de volta. Use seu email corporativo pra acessar o painel.
      </p>

      <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
        <Field label="Email" htmlFor="email">
          <div className="relative">
            <Mail
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-2"
              aria-hidden
            />
            <TextInput
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              placeholder="voce@empresa.com.br"
              className="w-full pl-9"
            />
          </div>
        </Field>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <label
              htmlFor="password"
              className="text-xs font-semibold text-ink-2"
            >
              Senha
            </label>
            <Link
              href="/auth/forgot-password"
              className="-my-3 py-3 text-xs font-semibold text-action hover:underline lg:-my-1 lg:py-1"
            >
              Esqueci minha senha
            </Link>
          </div>
          <div className="relative">
            <Lock
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-2"
              aria-hidden
            />
            <TextInput
              id="password"
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              placeholder="••••••••"
              className="w-full pr-11 pl-9"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute top-1/2 right-0 flex size-11 -translate-y-1/2 items-center justify-center rounded-md text-ink-2 hover:text-ink md:size-9.5"
              aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
              tabIndex={-1}
            >
              {showPw ? (
                <EyeOff className="size-4" aria-hidden />
              ) : (
                <Eye className="size-4" aria-hidden />
              )}
            </button>
          </div>
        </div>

        <label className="flex min-h-11 cursor-pointer items-center gap-2.5 self-start text-sm text-ink-2 md:min-h-0">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            disabled={loading}
            className="size-4 accent-sel"
          />
          Lembrar de mim
        </label>

        {error && (
          <div role="alert">
            <Banner family="red" icon={CircleAlert} title={error} />
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={loading}
          className="w-full"
        >
          {loading ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            <>
              Entrar
              <ArrowRight aria-hidden />
            </>
          )}
        </Button>
      </form>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  );
}
